import React from 'react';
import { Check } from 'lucide-react';
import { cn } from '@/lib/utils';
import { AutoplayABWizardStep } from '@/store/autoplay-ab-test/types';

interface AutoplayABWizardStepperProps {
  steps: AutoplayABWizardStep[];
  currentStep: number;
  onStepClick: (stepId: number) => void;
}

export function AutoplayABWizardStepper({ steps, currentStep, onStepClick }: AutoplayABWizardStepperProps) {
  const isStepAccessible = (stepId: number) => {
    if (stepId === currentStep) return true;
    if (steps[stepId - 1]?.completed) return true;
    if (stepId === currentStep - 1) return true;
    return false;
  };

  return (
    <div className="relative z-20 h-[52px] py-2 border-b border-[#E8E8E8] dark:border-border flex items-center">
      {/* Mobile: Ultra-Compact */}
      <div className="lg:hidden w-full">
        <div className="flex items-center justify-start gap-0.5 px-4">
          {steps.map((step, index) => {
            const isActive = step.id === currentStep;
            const isCompleted = step.completed;
            const isAccessible = isStepAccessible(step.id);
            const isLast = index === steps.length - 1;
            
            return (
              <React.Fragment key={step.id}>
                <button
                  onClick={() => isAccessible && onStepClick(step.id)}
                  disabled={!isAccessible}
                  className={cn(
                    "flex items-center gap-1.5 h-[28px] px-2 rounded-full text-[11px] font-medium transition-all whitespace-nowrap",
                    isActive && "bg-primary/10 text-primary shadow-sm border border-primary/20",
                    isCompleted && !isActive && "text-primary hover:bg-primary/5",
                    !isActive && !isCompleted && isAccessible && "text-[#9CA3AF] hover:text-[#6B7280]",
                    !isAccessible && "text-[#9CA3AF]/50 cursor-not-allowed"
                  )}
                >
                  {isCompleted ? (
                    <div className="flex items-center justify-center w-3.5 h-3.5 rounded-full bg-primary/20 border border-primary/30">
                      <Check className="h-2 w-2 text-primary" strokeWidth={3} />
                    </div>
                  ) : isActive ? (
                    <div className="w-1.5 h-1.5 rounded-full bg-primary" />
                  ) : (
                    <div className="w-1 h-1 rounded-full bg-[#D1D5DB]" />
                  )}
                  <span>{step.title}</span>
                </button>

                {!isLast && (
                  <div className="w-4 h-[1px] bg-[#E5E7EB] mx-0.5 overflow-hidden">
                    <div className={cn(
                      "h-full bg-primary transition-all duration-300",
                      isCompleted ? "w-full" : "w-0"
                    )} />
                  </div>
                )}
              </React.Fragment>
            );
          })}
        </div>
      </div>

      {/* Desktop: Compact */}
      <div className="hidden lg:flex w-full items-center justify-start px-6">
        <div className="flex items-center gap-0.5">
          {steps.map((step, index) => {
            const isActive = step.id === currentStep;
            const isCompleted = step.completed;
            const isAccessible = isStepAccessible(step.id);
            const isLast = index === steps.length - 1;

            return (
              <React.Fragment key={step.id}>
                <button
                  onClick={() => isAccessible && onStepClick(step.id)}
                  disabled={!isAccessible}
                  className={cn(
                    "flex items-center gap-1.5 h-[32px] px-3 rounded-full text-[12px] font-medium transition-all whitespace-nowrap",
                    isActive && "bg-primary/10 text-primary shadow-sm border border-primary/20",
                    isCompleted && !isActive && "text-primary hover:bg-primary/5",
                    !isActive && !isCompleted && isAccessible && "text-[#9CA3AF] hover:text-[#6B7280]",
                    !isAccessible && "text-[#9CA3AF]/50 cursor-not-allowed"
                  )}
                >
                  {isCompleted ? (
                    <div className="flex items-center justify-center w-4 h-4 rounded-full bg-primary/20 border border-primary/30">
                      <Check className="h-2.5 w-2.5 text-primary" strokeWidth={3} />
                    </div>
                  ) : isActive ? (
                    <div className="w-1.5 h-1.5 rounded-full bg-primary" />
                  ) : (
                    <div className="w-1 h-1 rounded-full bg-[#D1D5DB]" />
                  )}
                  <span>{step.title}</span>
                </button>

                {!isLast && (
                  <div className="w-6 h-[1px] bg-[#E5E7EB] mx-0.5 overflow-hidden">
                    <div className={cn(
                      "h-full bg-primary transition-all duration-300",
                      isCompleted ? "w-full" : "w-0"
                    )} />
                  </div>
                )}
              </React.Fragment>
            );
          })}
        </div>
      </div>
    </div>
  );
}
